import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { FlaskConical, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AndroidTestSignup = ({ app }) => {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const tt = t.tester;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const message =
        `[TEST ANDROID] Richiesta iscrizione tester — ${app.name}\n` +
        `Email Google Play: ${form.email}\n\n` +
        (form.message ? `Messaggio: ${form.message}` : "(nessun messaggio)");
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message,
        kind: "android-test",
        app_name: app.name,
        lang,
      });
      toast.success(tt.ok);
      setForm({ name: "", email: "", message: "" });
      setOpen(false);
    } catch {
      toast.error(tt.ko);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-5 py-4 text-sm text-white placeholder:text-muted2/60 outline-none transition-colors duration-300 focus:border-neon";

  return (
    <>
      <Reveal delay={0.18} className="mt-6">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="android-test-button"
          className="group flex w-full items-center gap-4 border border-white/15 bg-surface px-6 py-4 text-left transition-colors duration-300 hover:border-[#3DDC84]/60"
        >
          <FlaskConical className="h-6 w-6 shrink-0 text-[#3DDC84]" />
          <span className="min-w-0">
            <span className="block font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 group-hover:text-[#3DDC84]">
              {tt.cta}
            </span>
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-widest text-muted2">{tt.ctaNote}</span>
          </span>
        </button>
      </Reveal>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="test-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-lg border border-white/15 bg-void p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="test-modal"
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={tt.close}
                data-testid="test-modal-close"
                className="absolute right-4 top-4 text-muted2 transition-colors duration-300 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#3DDC84]">Android</p>
              <h3 className="mt-2 font-display text-2xl font-bold uppercase tracking-tight" data-testid="test-modal-title">
                {tt.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted2">{tt.intro.replace("{app}", app.name)}</p>
              <p className="mt-3 border-l-2 border-[#3DDC84] pl-3 text-xs leading-relaxed text-muted2" data-testid="test-modal-warn">
                {tt.warn}
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="test-form">
                <input required value={form.name} onChange={set("name")} placeholder={tt.name} className={inputCls} data-testid="test-name-input" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder={tt.email} className={inputCls} data-testid="test-email-input" />
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder={tt.message} className={`${inputCls} resize-none`} data-testid="test-message-input" />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="test-submit-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#3DDC84] px-8 py-4 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform duration-300 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                  {sending ? tt.sending : tt.send}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AndroidTestSignup;
